import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  Package,
  FolderKanban,
  BookOpen,
  Archive,
  LogOut,
  ShoppingCart,
  Building2,
  FileText,
} from 'lucide-react';
import { signOut, useSession } from '@/lib/auth';
import { ThemeToggle } from './ThemeToggle';
import { NotificationProvider } from './NotificationContext';
import { NotificationBell } from './NotificationBell';

const navItems = [
  { to: '/', label: 'Dashboard', icon: Package, end: true },
  { to: '/projecten', label: 'Projecten', icon: FolderKanban },
  { to: '/inkoop', label: 'Inkoop', icon: ShoppingCart },
  { to: '/catalogus', label: 'Catalogus', icon: BookOpen },
  { to: '/teksten', label: 'Teksten', icon: FileText },
  { to: '/leveranciers', label: 'Leveranciers', icon: Building2 },
  { to: '/voorraad', label: 'Restvoorraad', icon: Archive },
];

export function Layout() {
  const { user } = useSession();
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut();
    navigate('/login', { replace: true });
  }

  return (
    <NotificationProvider>
    <div className="flex h-screen overflow-hidden bg-bmade-50 text-slate-800 dark:bg-bmade-950 dark:text-slate-100">
      <aside className="flex w-60 flex-col border-r border-bmade-200 bg-white/80 backdrop-blur dark:border-bmade-800 dark:bg-bmade-900/60">
        <div className="flex h-14 items-center gap-2 border-b border-bmade-200 px-4 dark:border-bmade-800">
          <div className="rounded-md bg-accent-500/15 p-1.5">
            <Package className="h-4 w-4 text-accent-600 dark:text-accent-400" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] uppercase tracking-wider text-bmade-500 dark:text-bmade-400">
              2BMade
            </span>
            <span className="text-sm font-semibold text-bmade-950 dark:text-white">
              Pakket-tool
            </span>
          </div>
        </div>
        <nav className="flex flex-1 flex-col gap-1 p-3 text-sm">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 transition-colors ${
                  isActive
                    ? 'bg-accent-500/10 text-accent-700 dark:bg-accent-500/15 dark:text-accent-300'
                    : 'text-bmade-700 hover:bg-bmade-100 hover:text-bmade-900 dark:text-bmade-300 dark:hover:bg-bmade-800/60 dark:hover:text-white'
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-bmade-200 p-3 text-xs dark:border-bmade-800">
          <div className="truncate text-bmade-700 dark:text-bmade-300" title={user?.email ?? ''}>
            {user?.email ?? '—'}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex items-center gap-1.5 rounded-md border border-bmade-200 px-2 py-1 text-bmade-700 transition-colors hover:border-bmade-400 hover:bg-bmade-100 dark:border-bmade-800 dark:text-bmade-300 dark:hover:border-bmade-600 dark:hover:bg-bmade-800"
            >
              <LogOut className="h-3 w-3" />
              Uitloggen
            </button>
            <ThemeToggle />
          </div>
        </div>
      </aside>
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center justify-end border-b border-bmade-200 bg-white/80 px-6 backdrop-blur dark:border-bmade-800 dark:bg-bmade-900/60">
          <NotificationBell />
        </header>
        <main className="flex-1 overflow-auto px-8 py-6">
          <Outlet />
        </main>
      </div>
    </div>
    </NotificationProvider>
  );
}
